import { useTranslation } from '@mukil/i18n';
import { Badge } from '@jumpstone/ui-react';
import type { EssActionFailure } from '../../data/use_ess_invites';
import type {
  EssEmployeeStatus,
  EssInvite,
  EssInviteAction,
} from '../../data/wagepro_store_ess_invites';

/**
 * The ONE owner of how self-service (ESS) status renders — the directory's
 * Self-service column, the profile access tab's status line, and its invite
 * history rows all import from here, so a status's tone/label can never
 * drift between them (docs/design-system/CLAUDE.md § "One canonical
 * component per status/badge concept").
 *
 * Tones carry meaning, identically for the employee summary and a history
 * row: `success` only for live/accepted access, `warning` only for an
 * expired invite (the one state that silently stops working and needs the
 * employer to act), `accent` for an invite in flight, `neutral` for the rest.
 *
 * Labels are written as literal `t('…')` calls rather than a key lookup map
 * so `wagepro-i18n:no-orphans` can see every key is used.
 */
type Tone = 'neutral' | 'accent' | 'success' | 'warning';

const ESS_STATUS_TONE: Record<EssEmployeeStatus, Tone> = {
  not_invited: 'neutral',
  no_email: 'neutral',
  pending: 'accent',
  expired: 'warning',
  linked: 'success',
  revoked: 'neutral',
};

/** A single invite row's display status — `expired` is a `pending` row past `expires_at`. */
export type EssInviteRowStatus = EssInvite['status'] | 'expired';

const ESS_INVITE_ROW_TONE: Record<EssInviteRowStatus, Tone> = {
  pending: 'accent',
  expired: 'warning',
  accepted: 'success',
  revoked: 'neutral',
};

export function EssStatusBadge({
  status,
}: {
  status: EssEmployeeStatus;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  let label: string;
  switch (status) {
    case 'not_invited':
      label = t('employees.ess_status_not_invited');
      break;
    case 'no_email':
      label = t('employees.ess_status_no_email');
      break;
    case 'pending':
      label = t('employees.ess_status_pending');
      break;
    case 'expired':
      label = t('employees.ess_status_expired');
      break;
    case 'linked':
      label = t('employees.ess_status_linked');
      break;
    case 'revoked':
      label = t('employees.ess_status_revoked');
      break;
  }
  return (
    <Badge variant={ESS_STATUS_TONE[status]} data-testid="ess-status-badge">
      {label}
    </Badge>
  );
}

export function EssInviteRowBadge({
  status,
}: {
  status: EssInviteRowStatus;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  let label: string;
  switch (status) {
    case 'pending':
      label = t('employees.ess_invite_status_pending');
      break;
    case 'expired':
      label = t('employees.ess_invite_status_expired');
      break;
    case 'accepted':
      label = t('employees.ess_invite_status_accepted');
      break;
    case 'revoked':
      label = t('employees.ess_invite_status_revoked');
      break;
  }
  return <Badge variant={ESS_INVITE_ROW_TONE[status]}>{label}</Badge>;
}

/** Label for the invite action a status allows — same wording on every surface. */
export function EssInviteActionLabel({
  action,
}: {
  action: EssInviteAction;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  switch (action) {
    case 'invite':
      return <>{t('employees.ess_send_invite')}</>;
    case 'resend':
      return <>{t('employees.ess_action_resend')}</>;
    case 'reinvite':
      return <>{t('employees.ess_action_reinvite')}</>;
  }
}

export const ESS_ACTION_ERROR_KEY: Record<EssActionFailure, string> = {
  entitlement_required: 'employees.ess_action_error_entitlement_required',
  no_email: 'employees.ess_action_error_no_email',
  already_linked: 'employees.ess_action_error_already_linked',
  not_found: 'employees.ess_action_error_not_found',
  forbidden: 'employees.ess_action_error_forbidden',
  // Same copy `workerTypeCodeMessage` renders for this ledgerCode.
  contractor_not_eligible: 'errors.contractor_not_eligible',
  error: 'employees.ess_action_error_generic',
};
